/**
 * Fait : la CONNEXION du fil au canal temps réel — une par écran, qui porte
 *   le flux de génération (début, blocs, fin, annulation) et sert aussi à
 *   demander l'annulation. Elle traduit les messages du canal en gestes
 *   simples pour l'écran, ATTRIBUTION comprise (fil et branche émetteurs),
 *   et prévient quand le lien se coupe.
 *
 * Ne fait pas : elle ne décide d'aucun droit (le serveur revalide à chaque
 *   abonnement), ne recharge rien elle-même — elle DIT que la génération
 *   est finie, l'écran recharge — et ne FILTRE pas : le canal porte le
 *   projet entier, c'est `flux-du-fil.ts` qui ne garde que la branche
 *   montrée. Elle ne survit pas au démontage.
 */

/** D'où vient un message de génération : le fil et la branche émetteurs. */
export interface AttributionDuFlux {
  readonly conversationId: string;
  readonly brancheId: string;
}

/** Ce que l'écran veut savoir du canal, dans ses mots à lui. */
export interface ÉcouteDuFil {
  surDebut: (generationId: string, flux: AttributionDuFlux) => void;
  surBloc: (texte: string, generationId: string, flux: AttributionDuFlux) => void;
  surFin: (flux: AttributionDuFlux) => void;
  surAnnulation: (raison: string, flux: AttributionDuFlux) => void;
  surCoupure: () => void;
}

/** Ce que l'écran garde de la connexion : de quoi annuler, et fermer. */
export interface ConnexionDuFil {
  annule(generationId: string): void;
  ferme(): void;
}

/**
 * Ouvre la connexion et s'abonne au projet.
 *
 * Le drapeau d'abandon est indispensable : sans lui, le double montage de
 * `StrictMode` faisait crier « lien coupé » à chaque ouverture — l'alerte
 * devenait indiscernable d'une vraie coupure (bloquant de la phase 4).
 */
export function ouvreLeCanalDuFil(projetId: string, ecoute: ÉcouteDuFil): ConnexionDuFil {
  const adresse = `${location.origin.replace(/^http/, 'ws')}/api/canal`;
  const prise = new WebSocket(adresse);
  let abandonnee = false;

  prise.addEventListener('open', () => {
    prise.send(
      JSON.stringify({ type: 'abonner_projet', emisLe: new Date().toISOString(), projetId }),
    );
  });

  prise.addEventListener('message', (evenement: MessageEvent<string>) => {
    const recu = JSON.parse(evenement.data) as {
      type?: string;
      generationId?: string;
      conversationId?: string;
      brancheId?: string;
      texte?: string;
      raison?: string;
    };

    // L'attribution accompagne CHAQUE message de génération (contrat du
    // canal) : un message qui n'en porterait pas ne correspondra à aucune
    // branche montrée, donc ne s'affichera jamais — refus par construction.
    const flux = { conversationId: recu.conversationId ?? '', brancheId: recu.brancheId ?? '' };

    if (recu.type === 'generation_debut' && recu.generationId !== undefined) {
      ecoute.surDebut(recu.generationId, flux);
    }
    if (recu.type === 'generation_bloc' && recu.generationId !== undefined) {
      ecoute.surBloc(recu.texte ?? '', recu.generationId, flux);
    }
    if (recu.type === 'generation_fin') ecoute.surFin(flux);
    if (recu.type === 'generation_annulee') {
      ecoute.surAnnulation(recu.raison ?? 'La génération a été annulée.', flux);
    }
  });

  prise.addEventListener('close', () => {
    if (!abandonnee) ecoute.surCoupure();
  });

  return {
    annule(generationId) {
      prise.send(
        JSON.stringify({
          type: 'annuler_generation',
          emisLe: new Date().toISOString(),
          generationId,
        }),
      );
    },
    ferme() {
      abandonnee = true;
      prise.close();
    },
  };
}
